import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/buttons/Button';
import { SelectField } from '../forms/controls/base-fields/SelectField';

export type DataTablePagerConfig =
	| {
			mode?: 'client';
			pageSize: number;
	  }
	| {
			mode: 'server';
			pageSize: number;
			currentPage: number;
			totalCount: number;
			onPageChange: (page: number) => void;
			onPageSizeChange: (pageSize: number) => void;
	  };

type DataTablePagerProps = {
	currentPage: number;
	totalPages: number;
	totalCount: number;
	pageSize: number;
	pageSizeOptions?: number[];
	onPageChange: (page: number) => void;
	onPageSizeChange?: (pageSize: number) => void;
};

export function DataTablePager({
	currentPage,
	totalPages,
	totalCount,
	pageSize,
	pageSizeOptions = [10, 20],
	onPageSizeChange,
	onPageChange
}: DataTablePagerProps) {
	const startIndex = (currentPage - 1) * pageSize;
	const endIndex = startIndex + pageSize;
	const atStart = currentPage <= 1;
	const atEnd = currentPage >= totalPages;

	return (
		<div className="flex items-center justify-between border-t border-gray-100 bg-gray-50  px-3 py-2">
			<div className="text-xs text-gray-400">
				Showing page {currentPage} of {totalPages} ({totalCount} {totalCount === 1 ? 'item' : 'items'})
			</div>

			<div className="flex items-center gap-2">
				{onPageSizeChange && (
					<div className="flex items-center gap-1">
						<span className="text-xs text-gray-400">Page size:</span>
						<SelectField
							label="Page size"
							density="compact"
							hideLabel
							value={pageSize.toString()}
							containerClassName="mr-1"
							onChange={id => onPageSizeChange(Number(id))}
							items={pageSizeOptions.map(size => ({
								id: size.toString(),
								name: size.toString()
							}))}
						/>
					</div>
				)}
				<div className="flex items-center gap-1">
					<span className="text-xs text-gray-400">Go to page:</span>
					<SelectField
						label="Go to page"
						density="compact"
						hideLabel
						value={currentPage.toString()}
						containerClassName="mr-1"
						onChange={id => onPageChange(Number(id))}
						items={Array.from({ length: totalPages }, (_, i) => ({
							id: (i + 1).toString(),
							name: (i + 1).toString()
						}))}
					/>
				</div>
				<div className="flex items-center gap-1">
					<Button
						appearance="ghost"
						intent="neutral"
						size="xs"
						onPress={() => onPageChange(currentPage - 1)}
						isDisabled={atStart}
						aria-label="Previous page"
						Icon={ChevronLeft}
					/>

					<Button
						appearance="ghost"
						intent="neutral"
						size="xs"
						onPress={() => onPageChange(currentPage + 1)}
						isDisabled={atEnd}
						aria-label="Next page"
						Icon={ChevronRight}
					/>
				</div>
			</div>
		</div>
	);
}
